/**
 * @fileoverview Bank Overview page — the bank's overall financial position, 5-year table or
 * single-year table.
 * @description Ported from the InsightBox Banking design system's own `BankOverview.tsx` — the
 * most state-heavy Banking page, kept for last per the module plan's build order.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  FISCAL_YEARS,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  OVERVIEW_METRIC_GROUPS,
  TrendCell,
  branchLabel,
  core,
  formatCr,
  formatNumber,
  formatPct,
  formatSignedPct,
  overviewMetricValue,
  resolveBankingAccessScope,
  resolveFy,
  sumAcrossYears,
  totalBusiness,
  trendLabel,
  useBankingData,
  yoyGrowth,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'

function formatByUnit(v: number, unit: 'cr' | 'pct' | 'int'): string {
  if (unit === 'pct') return formatPct(v)
  if (unit === 'int') return formatNumber(v)
  // No " Cr" suffix here — the group heading itself already reads "Key Financial Metrics
  // (₹ In Crores)", so repeating the unit on every row value would be redundant.
  return `₹${v.toFixed(2)}`
}

/**
 * @component
 */
function BankingOverviewPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)
  const is5Years = fyFilter === FIVE_YEAR_ID
  const fy = resolveFy(fyFilter)

  const header = (
    <PageHeader
      kicker={t('banking.overview.kicker')}
      title={t('banking.overview.title')}
      description={t('banking.overview.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const c = core(data, fy, branchId)
  const deposits = c.deposits
  const advances = c.advances
  const business = totalBusiness(data, fy, branchId)
  const netProfit = c.netProfit

  const depositsYoY = yoyGrowth(fy, (f) => core(data, f, branchId).deposits)
  const advancesYoY = yoyGrowth(fy, (f) => core(data, f, branchId).advances)
  const businessYoY = yoyGrowth(fy, (f) => totalBusiness(data, f, branchId))
  const profitYoY = yoyGrowth(fy, (f) => core(data, f, branchId).netProfit)

  // "5 Years" selected -> every KPI card becomes a FY21-FY25 sum instead of a single year's
  // snapshot. All four of these are additive ₹ Cr amounts, so summing them is meaningful (unlike a
  // ratio/percentage, which only ever shows the latest year, never summed).
  const depositsSum = sumAcrossYears((f) => core(data, f, branchId).deposits)
  const advancesSum = sumAcrossYears((f) => core(data, f, branchId).advances)
  const businessSum = sumAcrossYears((f) => totalBusiness(data, f, branchId))
  const netProfitSum = sumAcrossYears((f) => core(data, f, branchId).netProfit)

  const scopeLabel = branchLabel(branchId)
  const fyIdx = FISCAL_YEARS.indexOf(fy)
  const prevFyLabel = fyIdx > 0 ? FISCAL_YEARS[fyIdx - 1] : null
  const vsLabel = prevFyLabel ? t('banking.overview.vsPriorYear', { fy, prevFy: prevFyLabel }) : ''

  const kpis: BankingKpiStripItem[] = is5Years
    ? [
        {
          label: t('banking.overview.kpi.totalBusiness5Y'),
          value: formatCr(businessSum),
          sublabel: t('banking.overview.kpi.fiveYearCombined'),
          highlight: true,
        },
        {
          label: t('banking.overview.kpi.netProfit5Y'),
          value: formatCr(netProfitSum),
          sublabel: t('banking.overview.kpi.fiveYearCombined'),
        },
        {
          label: t('banking.overview.kpi.totalDeposits5Y'),
          value: formatCr(depositsSum),
          sublabel: t('banking.overview.kpi.fiveYearCombined'),
        },
        {
          label: t('banking.overview.kpi.totalAdvances5Y'),
          value: formatCr(advancesSum),
          sublabel: t('banking.overview.kpi.fiveYearCombined'),
        },
      ]
    : [
        {
          label: t('banking.overview.kpi.totalBusiness'),
          value: formatCr(business),
          sublabel:
            businessYoY !== null
              ? t('banking.overview.yoySuffix', { value: formatSignedPct(businessYoY), vsLabel })
              : t('banking.baseYear'),
          highlight: true,
          ...(businessYoY !== null && { trend: { direction: trendLabel(businessYoY) } }),
        },
        {
          label: t('banking.overview.kpi.netProfit'),
          value: formatCr(netProfit),
          sublabel:
            profitYoY !== null
              ? t('banking.overview.yoySuffix', { value: formatSignedPct(profitYoY), vsLabel })
              : t('banking.baseYear'),
          ...(profitYoY !== null && { trend: { direction: trendLabel(profitYoY) } }),
        },
        {
          label: t('banking.overview.kpi.totalDeposits'),
          value: formatCr(deposits),
          sublabel:
            depositsYoY !== null
              ? t('banking.overview.yoySuffix', { value: formatSignedPct(depositsYoY), vsLabel })
              : t('banking.baseYear'),
          ...(depositsYoY !== null && { trend: { direction: trendLabel(depositsYoY) } }),
        },
        {
          label: t('banking.overview.kpi.totalAdvances'),
          value: formatCr(advances),
          sublabel:
            advancesYoY !== null
              ? t('banking.overview.yoySuffix', { value: formatSignedPct(advancesYoY), vsLabel })
              : t('banking.baseYear'),
          ...(advancesYoY !== null && { trend: { direction: trendLabel(advancesYoY) } }),
        },
      ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        fill
        title={
          is5Years
            ? t('banking.overview.chartTitleFiveYear')
            : t('banking.overview.chartTitleYear', { fy })
        }
        subtitle={t('banking.overview.chartSubtitleScope', { scope: scopeLabel })}
      >
        <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-border">
          {is5Years ? (
            <table className="w-full min-w-[900px] border-collapse text-sm">
              <thead>
                <tr className="bg-accent text-white">
                  <th className="sticky top-0 rounded-tl-lg bg-accent px-4 py-2 text-left font-semibold">
                    {t('banking.overview.table.metric')}
                  </th>
                  {FISCAL_YEARS.map((f) => (
                    <th
                      key={f}
                      className="sticky top-0 bg-accent px-4 py-2 text-center font-semibold"
                    >
                      {f}
                    </th>
                  ))}
                  <th className="sticky top-0 rounded-tr-lg bg-accent px-4 py-2 text-right font-semibold">
                    {t('banking.overview.table.trend')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {OVERVIEW_METRIC_GROUPS.map((group) => (
                  <Fragment key={group.groupLabel}>
                    <tr className="bg-primary-light/50">
                      <td
                        colSpan={FISCAL_YEARS.length + 2}
                        className="px-4 py-1 text-xs font-bold tracking-wide text-primary uppercase"
                      >
                        {group.groupLabel}
                      </td>
                    </tr>
                    {group.rows.map((row, idx) => {
                      const series = FISCAL_YEARS.map((f) =>
                        overviewMetricValue(data, f, row.key, branchId),
                      )
                      return (
                        <tr key={row.key} className={idx % 2 === 0 ? 'bg-white' : 'bg-input-bg'}>
                          <td className="px-4 py-1.5 font-semibold text-text">{row.label}</td>
                          {series.map((v, i) => (
                            <td key={FISCAL_YEARS[i]} className="px-4 py-1.5 text-center text-text">
                              {formatByUnit(v, group.unit)}
                            </td>
                          ))}
                          <td className="px-4 py-1.5">
                            <TrendCell data={series} width={80} height={24} align="right" />
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[420px] border-collapse text-sm">
              {/* Fixing both columns at 50% (rather than letting the value column's default
                  auto-width absorb 100% of the leftover space) is what keeps the value from
                  sitting flush against the far right edge with a huge empty gap after the label —
                  it starts at the row's midpoint instead, matching the Bank Details table's
                  proportions. Left-aligned so it reads naturally from that starting point, not
                  right-hugging the column's own right edge. */}
              <colgroup>
                <col className="w-1/2" />
                <col className="w-1/2" />
              </colgroup>
              <thead>
                <tr className="bg-accent text-white">
                  <th className="sticky top-0 rounded-tl-lg bg-accent px-4 py-2 text-left font-semibold">
                    {t('banking.overview.table.metric')}
                  </th>
                  <th className="sticky top-0 rounded-tr-lg bg-accent px-4 py-2 text-left font-semibold">
                    {fy}
                  </th>
                </tr>
              </thead>
              <tbody>
                {OVERVIEW_METRIC_GROUPS.map((group) => (
                  <Fragment key={group.groupLabel}>
                    <tr className="bg-primary-light/50">
                      <td
                        colSpan={2}
                        className="px-4 py-1 text-xs font-bold tracking-wide text-primary uppercase"
                      >
                        {group.groupLabel}
                      </td>
                    </tr>
                    {group.rows.map((row, idx) => {
                      const cellValue = formatByUnit(
                        overviewMetricValue(data, fy, row.key, branchId),
                        group.unit,
                      )
                      return (
                        <tr key={row.key} className={idx % 2 === 0 ? 'bg-white' : 'bg-input-bg'}>
                          <td className="px-4 py-1.5 font-semibold text-text">{row.label}</td>
                          <td className="px-4 py-1.5 text-left font-bold text-text">{cellValue}</td>
                        </tr>
                      )
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        variant="plain"
        title={t('banking.overview.businessSummaryTitle', { fy })}
        lines={
          is5Years
            ? [
                t('banking.overview.summary.fiveYearLine1', { business: formatCr(business), fy }),
                t('banking.overview.summary.fiveYearLine2', {
                  businessGrowth: formatSignedPct(
                    ((totalBusiness(data, 'FY25', branchId) -
                      totalBusiness(data, 'FY21', branchId)) /
                      totalBusiness(data, 'FY21', branchId)) *
                      100,
                  ),
                  profitGrowth: formatSignedPct(
                    ((core(data, 'FY25', branchId).netProfit -
                      core(data, 'FY21', branchId).netProfit) /
                      core(data, 'FY21', branchId).netProfit) *
                      100,
                  ),
                }),
                t('banking.overview.summary.fiveYearLine3', {
                  business: formatCr(businessSum),
                  profit: formatCr(netProfitSum),
                }),
              ]
            : [
                t('banking.overview.summary.yearLine1', {
                  scope: scopeLabel === 'All Branches' ? t('banking.overview.theBank') : scopeLabel,
                  business: formatCr(business),
                  fy,
                  yoyClause:
                    businessYoY !== null
                      ? t('banking.overview.summary.yoyClause', {
                          direction:
                            businessYoY >= 0
                              ? t('banking.overview.summary.growing')
                              : t('banking.overview.summary.declining'),
                          value: formatSignedPct(businessYoY),
                        })
                      : '',
                  netProfit: formatCr(netProfit),
                  profitYoyClause:
                    profitYoY !== null
                      ? t('banking.overview.summary.profitYoyClause', {
                          value: formatSignedPct(profitYoY),
                        })
                      : '',
                }),
                t('banking.overview.summary.yearLine2'),
              ]
        }
      />
    </div>
  )
}

export default BankingOverviewPage
